import {getUserAccountProfileExistStatus} from '@/api/api';
import {logLoginEvent} from '@/common/analytics-events';
import {ArrowButton} from '@/components/Buttons/ArrowButton';
import {InternationalPhoneInput} from '@/components/InternationalPhoneInput/InternationalPhoneInput';
import {PrivacyText} from '@/components/PrivacyText/PrivacyText';
import {BasePhoneVerification} from '@/pages/SignInSignUp/PhoneNumberAddVerification/BasePhoneVerification';
import {setAccessToken, setCurrentAccountId} from '@/user/user-utils';
import {yupResolver} from '@hookform/resolvers/yup';
import {PhoneAuthProvider, signInWithCredential, updateEmail} from 'firebase/auth';
import {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import * as yup from 'yup';
import {handlePostSignUp} from '../../firebase/auth-utils';
import {auth as firebaseAuth} from '../../firebase/config';
import {useAppDispatch} from '../../redux/reduxUtils';
import {setSiteId} from '../../redux/slices/queryParams';

export const MLSAuthSignup = () => {
  const validationSchema = yup.object().shape({
    email: yup.string().required('Email is required').email('Email is invalid'),
    phone: yup.string().required('Phone is required'),
  });
  const [step, setStep] = useState<'collectInfo' | 'verifyPhone'>('collectInfo');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isAccountExists, setIsAccountExists] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const mboClientIds = searchParams.get('mboClientIds');
  const providedEmail = searchParams.get('email')?.trim();
  const siteId = searchParams.get('siteId');

  const validatedEmail = providedEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(providedEmail) ? providedEmail : null;

  useEffect(() => {
    if (siteId) {
      dispatch(setSiteId(siteId));
    }
  }, [siteId, dispatch]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: {errors},
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: validatedEmail || '',
      phone: '',
    },
  });

  const watchPhone = watch('phone');

  const handleCollectInfoContinue = async (data: any) => {
    try {
      setIsLoading(true);
      const emailToUse = validatedEmail || data.email;
      // Format phone number to remove only spaces, parentheses and hyphens, keeping the plus sign
      const formattedPhone = data.phone.replace(/[\s()-]/g, '');
      const response = await getUserAccountProfileExistStatus({
        email: emailToUse,
        phoneNumber: formattedPhone,
      });
      if (response.data.accountExists) {
        setIsAccountExists(true);
      } else {
        setStep('verifyPhone');
      }
    } catch (error) {
      console.error(error);
      setErrorMessage('Something went wrong, please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const updateUserEmail = async (user: any, email: string) => {
    try {
      await updateEmail(user, email);
    } catch (error) {
      setErrorMessage('Failed to update email on firebase');
      throw error;
    }
  };

  const handlePhoneVericfiationComplete = async (
    phoneNumber: string,
    verificationId: string,
    verificationCode: string
  ) => {
    try {
      setIsLoading(true);
      const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
      const userCredential: any = await signInWithCredential(firebaseAuth, credential);
      const authUser = userCredential.user;
      setCurrentAccountId(authUser.uid);
      setAccessToken(authUser.accessToken);
      const isNewAccount = userCredential._tokenResponse.isNewUser;

      logLoginEvent(isNewAccount ? 'phone-signup' : 'phone-login', {
        userUid: userCredential?.user?.uid,
        operationType: userCredential?.operationType,
      });

      if (isNewAccount) {
        await updateUserEmail(authUser, validatedEmail || getValues('email'));
      }

      const route = await handlePostSignUp(userCredential);
      navigate(route);
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className="bg-black">
      <div className="flex relative flex-col min-h-screen max-w-lg mx-auto flex-1 text-white items-center justify-center px-4">
        <ArrowButton
          onClick={() => navigate('/auth')}
          className="absolute top-4 left-2"
          arrowDirection="left"
          variant="secondary"
        />
        {step === 'collectInfo' && (
          <div className="collectInfoStep">
            <h1 className="text-5xl font-teko text-center font-bold">Enter Your Info</h1>
            <p className="mt-2 font-helvetica font-thin text-base mb-4 text-center">
              Please provide both your Email and Phone Number
            </p>

            <form onSubmit={handleSubmit(handleCollectInfoContinue)} className="flex flex-col w-full">
              {validatedEmail && (
                <p className="text-lg mt-4 block font-light">
                  Email: <span className="font-semibold">{validatedEmail}</span>
                </p>
              )}

              {!validatedEmail && (
                <>
                  <label className="text-sm font-helvetica font-thin opacity-30 ml-1">Email</label>
                  <input
                    className="text-black border p-3 rounded !mt-2 text-sm"
                    type="email"
                    placeholder="Email address"
                    {...register('email')}
                    onChange={(e) => {
                      setValue('email', e.target.value, {shouldValidate: true});
                      setErrorMessage('');
                      setIsAccountExists(false);
                    }}
                    data-test-id="signup-email-input"
                  />
                </>
              )}
              <label className="text-sm font-helvetica font-thin opacity-30 ml-1 mt-4">Phone Number</label>
              <InternationalPhoneInput
                value={watchPhone || ''}
                onChange={(val) => {
                  setValue('phone', val, {shouldValidate: true});
                  setErrorMessage('');
                  setIsAccountExists(false);
                }}
                defaultCountry="us"
                disabled={isLoading}
                className="!mt-2"
                data-test-id="signup-phone-input"
              />
              {isAccountExists && (
                <p className="text-mls-error text-sm mt-3">
                  Account already exists with this email or phone number,{' '}
                  <Link className="underline text-blue-500" to="/auth/signin">
                    please sign in here.
                  </Link>
                </p>
              )}
              {errorMessage && <p className="text-mls-error text-sm mt-3">{errorMessage}</p>}
            </form>
            <PrivacyText className="mt-12 mb-4 text-white" darkMode actionButtonText="the arrow below" />
            <div className="flex justify-end mt-6">
              <ArrowButton
                className="bg-primary"
                onClick={handleSubmit(handleCollectInfoContinue)}
                arrowDirection="right"
                variant="primary"
                loading={isLoading}
                data-test-id="signup-continue-button"
              />
            </div>
          </div>
        )}
        {step === 'verifyPhone' && (
          <div className="verifyPhoneStep">
            <BasePhoneVerification
              phoneNumber={getValues('phone')}
              onVerificationComplete={handlePhoneVericfiationComplete}
              phoneInputHeader={
                <>
                  <h1 className="text-3xl text-center font-bold">Create your account with phone number</h1>
                  <p className="mt-4 font-helvetica font-thin text-base mb-8 text-center">
                    Enter your phone number to confirm and verify your account
                  </p>
                </>
              }
              verificationCodeHeader={({phoneNumber}) => (
                <>
                  <h1 className="text-3xl text-center font-bold mb-2">ENTER YOUR VERIFICATION CODE</h1>
                  <p className="mt-2 font-helvetica font-thin text-base mb-8 text-center">
                    A code was sent to {phoneNumber}
                  </p>
                </>
              )}
              phoneSubmitButton={({isSubmitting, onSubmit, hasError}) => (
                <ArrowButton
                  className="fixed bottom-8 right-4"
                  onClick={onSubmit}
                  loading={isSubmitting}
                  arrowDirection="right"
                  variant="primary"
                  hasError={hasError}
                  data-test-id="signup-phone-submit-button"
                />
              )}
              verificationSubmitButton={({isSubmitting, onSubmit, hasError}) => (
                <ArrowButton
                  className="absolute bottom-8 right-4"
                  onClick={onSubmit}
                  loading={isSubmitting}
                  arrowDirection="right"
                  variant="primary"
                  hasError={hasError}
                  data-test-id="signup-verification-submit-button"
                />
              )}
            />
            {errorMessage && <p className="text-mls-error text-xs mt-3">{errorMessage}</p>}
          </div>
        )}
      </div>
    </div>
  );
};
